import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import BottomNav from '../components/BottomNav';
import AppointmentCard from '../components/AppointmentCard';
import AddAppointmentModal from '../components/AddAppointmentModal';
import AddBlockModal from '../components/AddBlockModal';
import PanelToast from '../components/PanelToast';
import BookingLinkShareModal from '../components/BookingLinkShareModal';
import DashboardTodayStats from '../components/DashboardTodayStats';
import DashboardRollingStats from '../components/DashboardRollingStats';
import { openPhoneDialer, openSmsComposer } from '../utils/deviceActions';
import { isAppointmentResolved } from '../utils/appointmentStatus';
import { buildPublicBookingUrl, isBookingSlugValid, normalizeBookingSlug, resolveBookingOrigin } from '../utils/booking';

function formatPrice(value) {
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function normalizeBlockInterval(block) {
  const start = new Date(block.blocked_from);
  const end = new Date(block.blocked_until);
  const isVacation = block.block_type === 'vacation' || String(block.reason || '').trim().toLowerCase() === 'urlop';

  if (!isVacation) {
    return { start, end };
  }

  const normalizedStart = new Date(start);
  normalizedStart.setHours(0, 0, 0, 0);
  const normalizedEnd = new Date(end);
  normalizedEnd.setHours(23, 59, 59, 999);
  return { start: normalizedStart, end: normalizedEnd };
}

function getFreeWindowsCount(appointments, schedule, blocks, dayDate) {
  const dayOfWeek = dayDate.getDay();
  const daySchedule = schedule.filter((slot) => slot.day_of_week === dayOfWeek);
  if (daySchedule.length === 0) return 0;

  const dayStart = new Date(dayDate);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayDate);
  dayEnd.setHours(23, 59, 59, 999);

  const dayBlocks = blocks.filter((block) => {
    const blockInterval = normalizeBlockInterval(block);
    return blockInterval.start <= dayEnd && blockInterval.end >= dayStart;
  });

  const sortedAppointments = appointments
    .filter((appt) => appt.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

  let windows = 0;

  for (const slot of daySchedule) {
    const [startHour, startMinute] = slot.start_time.slice(0, 5).split(':').map(Number);
    const [endHour, endMinute] = slot.end_time.slice(0, 5).split(':').map(Number);
    const periodStart = new Date(dayDate);
    periodStart.setHours(startHour, startMinute, 0, 0);
    const periodEnd = new Date(dayDate);
    periodEnd.setHours(endHour, endMinute, 0, 0);

    const busyIntervals = [
      ...sortedAppointments.map((appt) => ({
        start: new Date(appt.scheduled_at),
        end: new Date(new Date(appt.scheduled_at).getTime() + appt.duration_minutes * 60000),
      })),
      ...dayBlocks.map((block) => normalizeBlockInterval(block)),
    ]
      .filter((interval) => interval.start < periodEnd && interval.end > periodStart)
      .sort((a, b) => a.start - b.start);

    let cursor = new Date(periodStart);
    for (const interval of busyIntervals) {
      if (interval.start > cursor) {
        windows += 1;
      }
      if (interval.end > cursor) {
        cursor = interval.end;
      }
    }

    if (cursor < periodEnd) {
      windows += 1;
    }
  }

  return windows;
}

function getDayBlocks(blocks, dayDate) {
  const dayStart = new Date(dayDate);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayDate);
  dayEnd.setHours(23, 59, 59, 999);

  return blocks.filter((block) => {
    const blockInterval = normalizeBlockInterval(block);
    return blockInterval.start <= dayEnd && blockInterval.end >= dayStart;
  });
}

function getBlockTone(block) {
  if (!block) {
    return 'custom';
  }

  if (block.block_type === 'vacation' || String(block.reason || '').trim().toLowerCase() === 'urlop') {
    return 'vacation';
  }

  if (block.block_type === 'break') {
    return 'break';
  }

  if (block.block_type === 'private') {
    return 'private';
  }

  return 'custom';
}

function formatBlockTime(block) {
  const { start, end } = normalizeBlockInterval(block);

  if (start.getHours() === 0 && start.getMinutes() === 0 && end.getHours() === 23 && end.getMinutes() === 59) {
    return 'cały dzień';
  }

  return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
}

export default function DashboardPage() {
  const { professional, updateProfessional } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const onboardingDismissKey = professional?.id ? `clientway-onboarding-dismissed:${professional.id}` : '';
  const [todayAppts, setTodayAppts] = useState([]);
  const [daySummary, setDaySummary] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [onboardingSnapshot, setOnboardingSnapshot] = useState({
    active_services_count: 0,
    total_services_count: 0,
    active_schedule_days_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showAddAppt, setShowAddAppt] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [appointmentModalDate, setAppointmentModalDate] = useState(() => new Date());
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [toast, setToast] = useState(null);
  const [slugDraft, setSlugDraft] = useState(() => professional?.booking_slug || '');
  const [slugError, setSlugError] = useState('');
  const [isSavingSlug, setIsSavingSlug] = useState(false);
  const [isOnboardingDismissed, setIsOnboardingDismissed] = useState(() => {
    if (typeof window === 'undefined' || !professional?.id) {
      return false;
    }

    return window.localStorage.getItem(`clientway-onboarding-dismissed:${professional.id}`) === '1';
  });

  const todayLabel = format(currentDate, "EEEE, d MMMM yyyy", { locale: pl });
  const isInteractionLocked = showAddAppt || showAddBlock || showShareModal;

  const fetchData = useCallback(async (referenceDate = new Date()) => {
    const dateKey = format(referenceDate, 'yyyy-MM-dd');

    setCurrentDate((previousDate) => (
      format(previousDate, 'yyyy-MM-dd') === dateKey ? previousDate : new Date(referenceDate)
    ));

    try {
      const response = await api.get(`/appointments/live-summary?date=${dateKey}`);
      const payload = response.data || {};
      const appointmentsData = Array.isArray(payload.appointments) ? payload.appointments : [];
      const sortedToday = [...appointmentsData].sort((a, b) => {
        const aResolved = isAppointmentResolved(a);
        const bResolved = isAppointmentResolved(b);
        if (aResolved !== bResolved) {
          return aResolved ? 1 : -1;
        }
        return new Date(a.scheduled_at) - new Date(b.scheduled_at);
      });
      setTodayAppts(sortedToday);
      setDaySummary(payload.day_summary || null);
      setPendingCount(Number(payload.pending_count || 0));
      setStats(payload.stats || null);
      setSchedule(Array.isArray(payload.schedule) ? payload.schedule : []);
      setBlocks(Array.isArray(payload.blocks) ? payload.blocks : []);
      setOnboardingSnapshot({
        active_services_count: Number(payload.onboarding?.active_services_count || 0),
        total_services_count: Number(payload.onboarding?.total_services_count || 0),
        active_schedule_days_count: Number(payload.onboarding?.active_schedule_days_count || 0),
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    if (isInteractionLocked) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      fetchData(new Date());
    }, 30000);

    function handleVisibilityChange() {
      if (!document.hidden && !isInteractionLocked) {
        fetchData(new Date());
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData, isInteractionLocked]);

  useEffect(() => {
    setSlugDraft(professional?.booking_slug || '');
  }, [professional?.booking_slug]);

  useEffect(() => {
    if (typeof window === 'undefined' || !onboardingDismissKey) {
      setIsOnboardingDismissed(false);
      return;
    }

    setIsOnboardingDismissed(window.localStorage.getItem(onboardingDismissKey) === '1');
  }, [onboardingDismissKey]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('welcome') !== '1') {
      return;
    }

    setToast({
      type: 'success',
      message: 'Konto zostało utworzone. Możesz od razu ustawić własny link i udostępnić rezerwacje klientkom.',
    });

    searchParams.delete('welcome');
    const nextSearch = searchParams.toString();
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  function handleCallClick(phone) {
    openPhoneDialer(phone);
  }

  function handleSmsClick(phone, text) {
    openSmsComposer(phone, text);
  }

  async function handleSaveBookingSlug() {
    const nextSlug = normalizeBookingSlug(slugDraft);

    if (!isBookingSlugValid(nextSlug)) {
      setSlugError('Adres musi mieć 3-50 znaków i może zawierać tylko małe litery, cyfry oraz myślniki.');
      return;
    }

    setIsSavingSlug(true);
    setSlugError('');

    try {
      const { data } = await api.patch('/auth/me/booking-profile', { booking_slug: nextSlug });
      updateProfessional(data);
      setSlugDraft(data.booking_slug || '');
      setToast({
        type: 'success',
        message: data.booking_slug === nextSlug
          ? 'Adres rezerwacji został zapisany.'
          : `Adres był zajęty, zapisano: ${data.booking_slug}`,
      });
    } catch (err) {
      setSlugError(err.response?.data?.error || 'Nie udało się zapisać adresu rezerwacji.');
    } finally {
      setIsSavingSlug(false);
    }
  }

  async function handleStatusChange(id, status) {
    try {
      await api.put(`/appointments/${id}`, { status });
      setToast({ type: 'success', message: 'Status wizyty został zaktualizowany.' });
      await fetchData(new Date());
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Nie udało się zmienić statusu wizyty.' });
    }
  }

  const normalizedSlugDraft = normalizeBookingSlug(slugDraft);
  const currentBookingSlug = professional?.booking_slug || '';
  const bookingUrl = buildPublicBookingUrl(currentBookingSlug);
  const bookingUrlPrefix = `${resolveBookingOrigin()}/book/`;
  const isSlugDirty = normalizedSlugDraft !== currentBookingSlug;
  const firstName = (professional?.name || '').trim().split(/\s+/)[0] || 'witaj';
  const createdAt = professional?.created_at ? new Date(professional.created_at) : null;
  const isNewAccount = createdAt && Number.isFinite(createdAt.getTime())
    ? Date.now() - createdAt.getTime() < 1000 * 60 * 60 * 24 * 14
    : false;
  const defaultGeneratedSlug = professional?.id ? normalizeBookingSlug(`${professional?.name || ''}-${professional.id}`) : '';
  const hasCustomBookingSlug = Boolean(currentBookingSlug) && currentBookingSlug !== defaultGeneratedSlug;
  const hasActiveServices = onboardingSnapshot.active_services_count > 0;
  const hasActiveSchedule = onboardingSnapshot.active_schedule_days_count > 0;
  const showOnboarding = isNewAccount && !isOnboardingDismissed;
  const onboardingSteps = [
    {
      key: 'slug',
      label: hasCustomBookingSlug ? 'Masz własny adres rezerwacji' : 'Ustaw własny adres rezerwacji',
      done: hasCustomBookingSlug,
    },
    {
      key: 'services',
      label: hasActiveServices
        ? `Oferta aktywna: ${onboardingSnapshot.active_services_count} usług`
        : 'Dodaj pierwszą usługę do oferty',
      done: hasActiveServices,
    },
    {
      key: 'schedule',
      label: hasActiveSchedule
        ? `Grafik aktywny: ${onboardingSnapshot.active_schedule_days_count} dni pracy`
        : 'Uzupełnij dni i godziny pracy',
      done: hasActiveSchedule,
    },
  ];
  const completedOnboardingSteps = onboardingSteps.filter((step) => step.done).length;
  const todayBlocks = useMemo(() => getDayBlocks(blocks, currentDate), [blocks, currentDate]);
  const todayOverview = useMemo(() => {
    const plannedAppointments = todayAppts.filter((appt) => appt.status === 'scheduled');
    const freeWindows = getFreeWindowsCount(plannedAppointments, schedule, blocks, currentDate);
    const workMinutes = Number(daySummary?.work_minutes || 0);
    const workHours = Math.floor(workMinutes / 60);
    const workMinutesRemainder = workMinutes % 60;
    const workTimeLabel = workMinutes > 0
      ? `${workHours > 0 ? `${workHours}h ` : ''}${workMinutesRemainder}min`
      : '0 min';

    return {
      totalCount: Number(daySummary?.total_count || todayAppts.length || 0),
      workTimeLabel,
      visitsValue: Number(daySummary?.visits_value || 0),
      freeWindows: Number(daySummary?.free_slot_count ?? freeWindows),
    };
  }, [blocks, currentDate, daySummary, schedule, todayAppts]);
  const primaryTodayBlock = todayBlocks[0] || null;
  const todayBlockTone = getBlockTone(primaryTodayBlock);
  const todayKpis = [
    { key: 'today-total', label: 'Wizyty dziś', value: todayOverview.totalCount, featured: true },
    { key: 'today-windows', label: 'Wolne okna', value: todayOverview.freeWindows, tone: 'neutral' },
    { key: 'today-work', label: 'Czas pracy dziś', value: todayOverview.workTimeLabel, valueClassName: 'kpi-card-value--nowrap', tone: 'soft' },
    {
      key: 'today-value',
      label: 'Wartość wizyt dziś',
      value: formatPrice(todayOverview.visitsValue),
      unit: 'zł',
      valueClassName: 'kpi-card-value--price',
      tone: 'accent',
    },
  ];
  const rollingStatsKpis = [
    { key: 'stats-scheduled', label: 'Zaplanowane wizyty', value: stats?.scheduled_count || 0, tone: 'neutral' },
    { key: 'stats-completed', label: 'Zakończone wizyty', value: stats?.completed_count || 0, tone: 'soft' },
    { key: 'stats-accepted', label: 'Przyjęte prośby', value: stats?.accepted_count || 0, tone: 'neutral' },
    {
      key: 'stats-value',
      label: 'Wartość wizyt 30 dni',
      value: Math.round(stats?.revenue_total || 0),
      unit: 'zł',
      valueClassName: 'kpi-card-value--price',
      tone: 'accent',
    },
  ];

  return (
    <div className="panel-page dashboard-page">
      <PanelToast toast={toast} onClose={() => setToast(null)} />

      <div className="panel-header">
        <div className="panel-header-copy">
          <span className="panel-eyebrow">Panel ClientWay</span>
          <h1 className="panel-title">Dzień dobry, {firstName}</h1>
          <p className="panel-subtitle">{todayLabel} · plan dnia, rezerwacje i wolne okna w jednym miejscu.</p>
        </div>
        {pendingCount > 0 && (
          <a href="/panel/requests" className="badge-alert">
            {pendingCount} nowych próśb
          </a>
        )}
      </div>

      <div className="quick-actions">
        <button className="quick-btn" onClick={() => {
          setAppointmentModalDate(new Date());
          setShowAddAppt(true);
        }}>
          Dodaj wizytę
        </button>
        <button className="quick-btn quick-btn-outline" onClick={() => setShowAddBlock(true)}>
          Przerwa / blokada
        </button>
      </div>

      {showOnboarding && (
        <section className="section onboarding-section">
          <div className="onboarding-card">
            <div className="onboarding-copy">
              <span className="panel-eyebrow">Szybki start</span>
              <h2>Twoje konto jest gotowe do pierwszych rezerwacji</h2>
              <p>
                Dodaliśmy startowy grafik i podstawowe usługi. Teraz ustaw własny adres, dopasuj ofertę i wyślij link klientkom.
              </p>
            </div>
            <div className="onboarding-progress-row">
              <strong>{completedOnboardingSteps}/3 kroków gotowe</strong>
              <span>{hasCustomBookingSlug ? 'Zostało już tylko dopracowanie oferty i udostępnienie linku.' : 'Największy efekt da teraz ustawienie własnego adresu.'}</span>
            </div>
            <div className="onboarding-status">
              {onboardingSteps.map((step) => (
                <span key={step.key} className={`onboarding-pill ${step.done ? 'is-done' : ''}`}>
                  {step.label}
                </span>
              ))}
            </div>
            <div className="onboarding-metrics">
              <div className="onboarding-metric">
                <strong>{onboardingSnapshot.active_services_count}</strong>
                <span>aktywnych usług</span>
              </div>
              <div className="onboarding-metric">
                <strong>{onboardingSnapshot.active_schedule_days_count}</strong>
                <span>dni pracy</span>
              </div>
              <div className="onboarding-metric">
                <strong>{todayOverview.freeWindows}</strong>
                <span>wolnych okien dziś</span>
              </div>
            </div>
            <div className="onboarding-actions">
              <a href="/panel/onboarding" className="btn-primary">Przejdź przez konfigurację</a>
              <a href="/panel/services" className="btn-secondary">Edytuj usługi</a>
              <a href="/panel/calendar" className="btn-secondary">Sprawdź grafik</a>
            </div>
            <button
              type="button"
              className="btn-ghost onboarding-dismiss"
              onClick={() => {
                setIsOnboardingDismissed(true);
                if (typeof window !== 'undefined' && onboardingDismissKey) {
                  window.localStorage.setItem(onboardingDismissKey, '1');
                }
              }}
            >
              Ukryj
            </button>
          </div>
        </section>
      )}

      <section className="section">
        <h2 className="section-title">Dzisiaj ({todayOverview.totalCount})</h2>
        <div className="dashboard-today-layout">
          <div className="dashboard-today-kpis">
            <DashboardTodayStats
              items={todayKpis}
              ariaLabel="Podsumowanie dnia"
            />
          </div>

          <div className="dashboard-today-primary">
            {todayBlocks.length > 0 && (
              <div className={`day-focus-banner day-focus-banner--${todayBlockTone}`}>
                <div className="day-focus-banner-copy">
                  <strong>{primaryTodayBlock?.reason || 'Blokada czasu'}</strong>
                  <span>
                    {todayBlocks.length === 1
                      ? `Aktywna blokada na dziś: ${formatBlockTime(primaryTodayBlock)}.`
                      : `Aktywnych blokad na dziś: ${todayBlocks.length}. Pierwsza: ${formatBlockTime(primaryTodayBlock)}.`}
                  </span>
                </div>
                <a href="/panel/calendar" className="btn-secondary day-focus-banner-link">Otwórz grafik</a>
              </div>
            )}

            {loading && <p className="loading-text">Ładowanie...</p>}

            {!loading && todayAppts.length === 0 && (
              <div className={`panel-empty-state ${todayBlocks.length > 0 ? 'panel-empty-state--blocked' : ''}`}>
                <strong>
                  {todayBlocks.length > 0
                    ? 'Ten dzień jest już zabezpieczony blokadą.'
                    : 'Brak wizyt na dziś.'}
                </strong>
                <p>
                  {todayBlocks.length > 0
                    ? `Masz ${todayBlocks.length === 1 ? '1 aktywną blokadę' : `${todayBlocks.length} aktywne blokady`}. Jeśli chcesz przyjąć klientkę, najpierw sprawdź grafik.`
                    : 'Możesz dodać wizytę ręcznie albo od razu wysłać klientkom swój link do rezerwacji.'}
                </p>
                <div className="panel-empty-state-actions">
                  {todayBlocks.length > 0 ? (
                    <a href="/panel/calendar" className="btn-secondary">Przejdź do grafiku</a>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        setAppointmentModalDate(new Date(currentDate));
                        setShowAddAppt(true);
                      }}
                    >
                      Dodaj wizytę
                    </button>
                  )}
                  <button type="button" className="btn-secondary" onClick={() => setShowShareModal(true)}>
                    Udostępnij link
                  </button>
                </div>
              </div>
            )}

            <div className="appt-list">
              {todayAppts.map((appt) => (
                <AppointmentCard
                  key={appt.id}
                  appointment={appt}
                  timeLabel={format(new Date(appt.scheduled_at), 'HH:mm')}
                  onCall={() => handleCallClick(appt.client_phone)}
                  onSms={() => handleSmsClick(appt.client_phone, `Cześć ${appt.client_name}! Przypominam o wizycie dziś o ${format(new Date(appt.scheduled_at), 'HH:mm')}. Do zobaczenia! 💅`)}
                  onSetStatus={(status) => handleStatusChange(appt.id, status)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="link-share-box">
        <p className="link-share-label">Twój link do rezerwacji:</p>
        <div className="booking-slug-editor">
          <div className="form-group booking-slug-group">
            <label htmlFor="bookingSlug">Końcówka adresu</label>
            <div className="booking-slug-row">
              <span className="booking-slug-prefix">{bookingUrlPrefix}</span>
              <input
                id="bookingSlug"
                type="text"
                value={slugDraft}
                onChange={(event) => {
                  setSlugDraft(normalizeBookingSlug(event.target.value));
                  setSlugError('');
                }}
                placeholder="nazwa-kosmetyczki"
                autoComplete="off"
                spellCheck="false"
              />
            </div>
            <p className="field-hint">To jest adres, który klientka zobaczy w linku i kodzie QR.</p>
            {slugError && <p className="field-error">{slugError}</p>}
          </div>
          <button
            type="button"
            className="btn-secondary booking-slug-save"
            onClick={handleSaveBookingSlug}
            disabled={isSavingSlug || !isSlugDirty || !normalizedSlugDraft}
          >
            {isSavingSlug ? 'Zapisywanie...' : 'Zapisz adres'}
          </button>
        </div>
        <div className="link-share-url">
          {bookingUrl}
        </div>
        <div className="link-share-actions">
          <button
            className="btn-secondary"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(bookingUrl);
                setToast({ type: 'success', message: 'Link do rezerwacji skopiowany.' });
              } catch {
                setToast({ type: 'error', message: 'Nie udało się skopiować linku.' });
              }
            }}
          >
            Kopiuj link
          </button>
          <button className="btn-secondary" onClick={() => setShowShareModal(true)}>
            Udostępnij
          </button>
        </div>
      </div>

      {stats && (
        <section className="section stats-section dashboard-secondary-section">
          <h2 className="section-title">Statystyki 30 dni</h2>
          <DashboardRollingStats
            items={rollingStatsKpis}
            ariaLabel="Statystyki 30 dni"
          />
        </section>
      )}

      {showAddAppt && (
        <AddAppointmentModal
          defaultDate={appointmentModalDate}
          onClose={() => setShowAddAppt(false)}
          onSaved={() => {
            setShowAddAppt(false);
            setToast({ type: 'success', message: 'Wizyta została dodana.' });
            void fetchData(new Date());
          }}
        />
      )}
      {showAddBlock && (
        <AddBlockModal
          onClose={() => setShowAddBlock(false)}
          onSaved={() => {
            setShowAddBlock(false);
            setToast({ type: 'success', message: 'Blokada czasu została dodana.' });
            void fetchData(new Date());
          }}
        />
      )}
      {showShareModal && (
        <BookingLinkShareModal
          bookingUrl={bookingUrl}
          professionalName={professional?.name}
          onClose={() => setShowShareModal(false)}
          onToast={setToast}
        />
      )}

      <BottomNav active="dashboard" />
    </div>
  );
}
